import { CalendarDays, FileChartColumn, LogOut, Settings, Ship, Users, WandSparkles } from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api, clearOfflineSnapshots } from "../api";
import { useI18n } from "../i18n";
import type { Session } from "../types";
import { BrandMark } from "./BrandMark";
import { useState, type ReactNode } from "react";
import { useOnlineStatus } from "../useOnlineStatus";
import { HelpAssistant } from "./HelpAssistant";
import { LanguageSelect } from "./LanguageSelect";
import { canUseSetupGuide, readSetupGuideState, SetupGuide, setupGuideCopy } from "./SetupGuide";

const navItems: Array<{ path: string; label: "today" | "departures" | "people" | "reports" | "settings"; icon: typeof CalendarDays; end: boolean; permission?: string }> = [
  { path: "/", label: "today", icon: CalendarDays, end: true },
  { path: "/departures", label: "departures", icon: Ship, end: false },
  { path: "/people", label: "people", icon: Users, end: false, permission: "participant:write" },
  { path: "/reports", label: "reports", icon: FileChartColumn, end: false, permission: "export:create" },
  { path: "/settings", label: "settings", icon: Settings, end: false, permission: "center:configure" }
] as const;

export function AppShell({ session, children }: { session: Session; children: ReactNode }) {
  const { t, language, setLanguage } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const online = useOnlineStatus();
  const canConfigure = canUseSetupGuide(session.permissions);
  const [guide, setGuide] = useState(() => {
    const saved = readSetupGuideState(session.user.id);
    return { open: canConfigure && saved === null, startAt: saved?.status === "paused" ? saved.step : 0, runId: 0 };
  });
  const openSetupGuide = () => {
    const saved = readSetupGuideState(session.user.id);
    setGuide((current) => ({ open: true, startAt: saved?.status === "paused" ? saved.step : 0, runId: current.runId + 1 }));
  };
  const signOut = async (): Promise<void> => {
    await api<void>("/api/v1/foundation/auth/logout", { method: "POST" });
    clearOfflineSnapshots();
    queryClient.clear();
    navigate("/login", { replace: true });
  };
  return (
    <div className="app-shell">
      <header className="topbar">
        {!online && <div className="offline-banner" role="status" aria-live="polite">{t("offline")} · {t("readOnlyMode")}</div>}
        <div className="topbar-main">
          <BrandMark />
          <div className="center-name">{session.tenant.displayName}</div>
          <div className="topbar-actions">
            {canConfigure && <button className="setup-guide-trigger" type="button" onClick={openSetupGuide}><WandSparkles size={19} /><span>{setupGuideCopy[language].trigger}</span></button>}
            <HelpAssistant onStartSetupGuide={canConfigure ? openSetupGuide : undefined} />
            <LanguageSelect />
            <div className="user-summary"><span className="avatar">{session.user.displayName.split(" ").map((part) => part[0]).join("").slice(0, 2)}</span><span>{session.user.displayName}</span></div>
            <button className="icon-button topbar-signout" onClick={signOut} aria-label={t("signOut")} title={t("signOut")}><LogOut size={20} /></button>
          </div>
        </div>
        <nav className="primary-nav" aria-label={t("primaryNavigation")}>
          {navItems.filter((item) => !item.permission || session.permissions.includes(item.permission)).map(({ path, label, icon: Icon, end }) => (
            <NavLink key={label} to={path} end={end}>
              <Icon size={21} /><span>{t(label)}</span>
            </NavLink>
          ))}
        </nav>
      </header>
      <main id="main-content" className="app-main">{children}</main>
      {canConfigure && <SetupGuide open={guide.open} startAt={guide.startAt} runId={guide.runId} userId={session.user.id} language={language} onLanguageChange={setLanguage} onClose={() => setGuide((current) => ({ ...current, open: false }))} />}
    </div>
  );
}
